import { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect } from 'expo-router';

import { useAuth } from '../../../context/AuthContext';
import { useCredits } from '../../../context/CreditContext';
import { useTrialContext } from '../../../context/TrialContext';
import { userProfileAPI } from '../../../services/api';
import { trackAuthEvent } from '../../../services/analytics';
import { showDialog } from '../../../utils/dialogs';
import type { Translate, UserProfile } from '../profileTypes';
import { shouldReportProfileLoadFailure } from './profileLoadFailure';

interface UseProfileDataOptions {
  t: Translate;
  /**
   * Runs in the same tick as the profile update, after every successful load,
   * so section forms can re-seed from the server copy. Read through a ref, so
   * it does not need to be stable.
   */
  onLoaded: (data: UserProfile) => void;
}

/** Loads the profile (with trial and credit status) and tracks screen views. */
export function useProfileData({ t, onLoaded }: UseProfileDataOptions) {
  const { refreshStatus: refreshCredits } = useCredits();
  const { refreshTrialStatus } = useTrialContext();
  const { phase, user } = useAuth();
  // Any phase with a user is signed in (a lapsed, paused or recovering subscriber still opens
  // Profile); only sign-out clears the user and moves to 'unauthenticated'.
  const signedIn = Boolean(user) && phase !== 'unauthenticated';

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const latestProfileRef = useRef<UserProfile | null>(null);
  const onLoadedRef = useRef(onLoaded);
  // Declared before the load effect, so a sign-out is seen before the reload it triggers.
  const signedInRef = useRef(signedIn);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
    signedInRef.current = signedIn;
  });

  useEffect(() => {
    latestProfileRef.current = profile;
  }, [profile]);

  useFocusEffect(
    useCallback(() => {
      trackAuthEvent('profile_screen_viewed', {
        user_type: latestProfileRef.current?.user_type,
        plan_tier: latestProfileRef.current?.subscription?.plan_tier,
        subscription_status: latestProfileRef.current?.subscription?.status,
      });
      return () => {};
    }, [])
  );

  const loadProfile = useCallback(async () => {
    // Signing out re-creates the credit/trial callbacks, which re-runs this
    // load on a screen that is about to unmount.
    // Stays in its loading state: the screen is about to unmount (sign-out) or the
    // load re-runs once the auth state settles.
    if (!signedInRef.current) return;
    try {
      const [data] = await Promise.all([
        userProfileAPI.getProfile(),
        refreshTrialStatus(),
        refreshCredits(),
      ]);
      const normalizedProfile: UserProfile = {
        ...data,
        subscription: data.subscription ?? {
          status: 'none',
          cancel_at_period_end: false,
          currency: 'usd',
        },
      };
      setProfile(normalizedProfile);
      onLoadedRef.current(data);
    } catch (error) {
      if (shouldReportProfileLoadFailure(error, signedInRef.current)) {
        showDialog(t('common.error'), t('profile.failedToLoad'));
      }
      console.error('Load profile error:', error);
    } finally {
      setLoading(false);
    }
  }, [refreshCredits, refreshTrialStatus, t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  return { profile, loading, loadProfile };
}
